/**
 * 発話区間（duel_speech_segments）の純関数。再現エンジンの入力画面（`components/ocgInput/speech.js` と発話タブ `SpeechTab.js`）が
 * 「シークの対象」「いま居る区間」「採否を切り替える区間」「LLM の提案の数」を決める（Issue #495 で共有に出した。#998 でシミュレータを消して入力画面だけに）。
 */

/**
 * 実在の話者ではない疑似 speaker（サーバーの `duel/speech_keep_ranges.py` の PSEUDO_SPEAKERS と対応）。
 * `effect` は演出区間で発話ではないのでシークの対象・レビューの一覧から外す。
 * `laughter` は「その時間帯に笑いが起きている」区間で、シーク先としては有効。
 */
const HIDDEN_SPEAKER = 'effect';
export const LAUGHTER_SPEAKER = 'laughter';

/** 話者名（笑いは誰の笑いか特定していないので専用表示） */
export function speechLabel(seg) {
  if (!seg) return '';
  return seg.speaker === LAUGHTER_SPEAKER ? '笑い' : seg.speaker;
}

/** シークの対象になる発話区間だけを開始時刻順に並べる */
export function seekableSegments(segments = []) {
  return segments
    .filter((s) => s.speaker !== HIDDEN_SPEAKER)
    .sort((a, b) => a.start_ms - b.start_ms);
}

/**
 * その時刻に「いま居る」発話区間の添字。
 *
 * **開始済みのうち一番新しいもの**を返す（区間の外＝間が空いているところでも
 * 直前の区間を指す）。動画の再生位置から出すので、区間と区間の間で
 * 表示が空になると「どこにいるか」を見失うため。まだ最初の区間より前なら -1。
 *
 * 区間は重なりうる（別の話者が同時に喋る）ので、開始時刻だけで決める。
 *
 * @param {{start_ms:number}[]} sorted 開始時刻の昇順に並んだ区間
 * @param {number} ms 動画の再生位置（ミリ秒）
 */
export function segmentIndexAt(sorted, ms) {
  let lo = 0, hi = sorted.length - 1, found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid].start_ms <= ms) { found = mid; lo = mid + 1; }
    else hi = mid - 1;
  }
  return found;
}

/** 前の区間の頭へ: 今の区間の頭より十分後ろ（1 秒）ならその区間の頭、そうでなければ 1 つ前 */
// 1 つ前は**添字ではなく開始時刻で**決める（別の話者が同時に喋り始めると開始時刻の同じ区間が並ぶ。添字を 1 つ戻すだけだと同じ時刻へ飛んで動かない）
export function prevSpeechMs(sorted, ms) {
  const i = segmentIndexAt(sorted, ms);
  if (i < 0) return null;
  const cur = sorted[i].start_ms;
  if (ms - cur > 1000) return cur;
  for (let j = i - 1; j >= 0; j -= 1) if (sorted[j].start_ms < cur) return sorted[j].start_ms;
  return sorted[0].start_ms;
}

export function nextSpeechMs(sorted, ms) {
  const i = segmentIndexAt(sorted, ms);
  return i + 1 < sorted.length ? sorted[i + 1].start_ms : null;
}

/**
 * 採否を切り替える区間（F キー。シミュレータの「直近の発話区間」と同じ: 開始済みのうち一番新しいもの）。無ければ null
 * @param {{start_ms:number}[]} sorted 開始時刻の昇順
 */
export function toggleTargetAt(sorted, ms) {
  const i = segmentIndexAt(sorted, ms);
  return i >= 0 ? sorted[i] : null;
}

/** LLM のテキスト修正の提案が付いている区間（一括の適用・却下の対象。数の表示も同じもの） */
export const proposalTargets = (segments = []) => segments.filter((s) => s.speaker !== HIDDEN_SPEAKER && s.proposed_text);

/** 一覧の頭に出す数: 発話（笑いを除く）・笑い・カット・提案 */
export function speechCounts(segments = []) {
  const shown = segments.filter((s) => s.speaker !== HIDDEN_SPEAKER);
  return {
    speech: shown.filter((s) => s.speaker !== LAUGHTER_SPEAKER).length,
    laughter: shown.filter((s) => s.speaker === LAUGHTER_SPEAKER).length,
    cut: shown.filter((s) => s.enabled === false).length,
    proposals: proposalTargets(shown).length,
  };
}

/** 話者ごとの色の並び（出てきた順。笑いは数えない） */
export const speakerOrder = (segments = []) => [...new Set(segments.filter((s) => s.speaker !== LAUGHTER_SPEAKER && s.speaker !== HIDDEN_SPEAKER).map((s) => s.speaker))];
